import {
  FilterParams,
  PaginatedParams
} from "src/library/utils";
import useAxios from "src/core/shared/hooks/useAxios";
import { IProduct } from "src/core/products/api/products.api";
import {
  RequestConfig,
  UseApiRequest,
  EndpointConfig
} from "src/core/shared/api/config.api";
import { PaginatedData } from "src/library/utils/apiTypes";
import cacheMinutesToMilliseconds from "src/library/utils/time-helpers/cache-minutes-to-milliseconds";

export type CreatePromotionParams = IPromotion;
export type UpdatePromotionParams = { promotionId: number, data: PartialPromotion };
export type PartialPromotion = Partial<IPromotion>;
export type ListPromotionsParams = {
  pagination: PaginatedParams<IPromotion>;
  filters: FilterParams<IPromotion>;
};

export interface IPromotion {
  id: number;
  name: string;
  template: PromotionTemplate;
  image: string;
  linkType: PromotionLinkType;
  categoryId?: number;
  productId?: number;
  linkUrl?: string;
  product?: IProduct;
  startDate: string;
  endDate: string;
}

export enum PromotionTemplate {
  Small = "small",
  Large = "large",
}

export enum PromotionLinkType {
  Product = "product",
  Category = "category",
  Raw = "raw"
}

export enum PromotionsRequestName {
  LIST_PROMOTIONS = "LIST_PROMOTIONS",
  LIST_PROMOTIONS_BY_DROP = "LIST_PROMOTIONS_BY_DROP",
  CREATE_PROMOTION = "CREATE_PROMOTION",
  UPDATE_PROMOTION = "UPDATE_PROMOTION",
  GET_PROMOTION = "GET_PROMOTION",
  DELETE_PROMOTION = "DELETE_PROMOTION"
}

type PromotionsEndpointConfig = {
  [PromotionsRequestName.LIST_PROMOTIONS]: EndpointConfig<
    ListPromotionsParams,
    PaginatedData<IPromotion[]>
  >;
  [PromotionsRequestName.LIST_PROMOTIONS_BY_DROP]: EndpointConfig<
    {dropId: number},
    IPromotion[]
  >,
  [PromotionsRequestName.CREATE_PROMOTION]: EndpointConfig<
    CreatePromotionParams,
    IPromotion
  >,
  [PromotionsRequestName.UPDATE_PROMOTION]: EndpointConfig<
    UpdatePromotionParams,
    IPromotion
  >,
  [PromotionsRequestName.GET_PROMOTION]: EndpointConfig<
    {id: number},
    IPromotion
  >,
  [PromotionsRequestName.DELETE_PROMOTION]: EndpointConfig<
    {id: number},
    IPromotion
  >
};

const BASE_URL = `${process.env.REACT_APP_API_URL}/promotion`;

/**
 * Promotions API
 */
const requests: RequestConfig<PromotionsEndpointConfig> = {
  [ PromotionsRequestName.LIST_PROMOTIONS ]: params => ({
    url: `${BASE_URL}/all`,
    method: "GET",
    params
  }),
  [ PromotionsRequestName.LIST_PROMOTIONS_BY_DROP ]: ({ dropId }) => ({
    url: `${BASE_URL}/branch/${dropId}`,
    method: "GET",
    cache: {
      maxAge: cacheMinutesToMilliseconds(30),
      clearOnError: true
    }
  }),
  [ PromotionsRequestName.CREATE_PROMOTION ]: data => ({
    url: `${BASE_URL}/create`,
    method: "POST",
    data
  }),
  [ PromotionsRequestName.UPDATE_PROMOTION ]: ({ promotionId, data }) => ({
    url: `${BASE_URL}/${promotionId}`,
    method: "PATCH",
    data
  }),
  [ PromotionsRequestName.GET_PROMOTION ]: ({ id }) => ({
    url: `${BASE_URL}/${id}`,
    method: "GET"
  }),
  [ PromotionsRequestName.DELETE_PROMOTION ]: ({ id }) => ({
    url: `${BASE_URL}/${id}`,
    method: "DELETE"
  })
};

export const usePromotionsApi: UseApiRequest<PromotionsEndpointConfig> = name => useAxios(requests[ name ], name);
